import { Box, IconButton, Paper, Stack, TextField as MuiTextField, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const EMPTY_LOCKED_KEYS = new Set<string>();

export const tryParseJsonString = (value: unknown) => {
  if (typeof value !== "string") {
    return value;
  }

  const normalizeLooseJson = (input: string) => {
    let normalized = input.trim();

    // Remove optional wrapping quotes for payloads serialized as a JSON string literal.
    if (
      (normalized.startsWith('"') && normalized.endsWith('"')) ||
      (normalized.startsWith("'") && normalized.endsWith("'"))
    ) {
      normalized = normalized.slice(1, -1);
    }

    normalized = normalized.replace(/\\n/g, "\n").replace(/\\r/g, "").replace(/\\t/g, "\t").replace(/\\\"/g, '"');

    // Quote unquoted object keys like IOS, ANDROID, WEB.
    normalized = normalized.replace(/([{,]\s*)([A-Za-z_][A-Za-z0-9_]*)(\s*:)/g, '$1"$2"$3');

    // Remove trailing commas before object/array close and dangling commas at the end.
    normalized = normalized.replace(/,\s*([}\]])/g, "$1").replace(/,\s*$/g, "");

    // Auto-close unmatched braces/brackets for partially stored payloads.
    const openCurly = (normalized.match(/\{/g) || []).length;
    const closeCurly = (normalized.match(/\}/g) || []).length;
    if (openCurly > closeCurly) {
      normalized += "}".repeat(openCurly - closeCurly);
    }

    const openSquare = (normalized.match(/\[/g) || []).length;
    const closeSquare = (normalized.match(/\]/g) || []).length;
    if (openSquare > closeSquare) {
      normalized += "]".repeat(openSquare - closeSquare);
    }

    return normalized;
  };

  try {
    return JSON.parse(value);
  } catch (error) {
    // Handle relaxed JSON-like payloads where keys are unquoted, commas trail, or braces are incomplete.
    const normalized = normalizeLooseJson(value);

    try {
      return JSON.parse(normalized);
    } catch (nestedError) {
      return value;
    }
  }
};

export const updateValueAtPath = (source: any, path: Array<string | number>, nextValue: string): any => {
  if (path.length === 0) {
    return nextValue;
  }

  const [head, ...rest] = path;

  if (Array.isArray(source)) {
    const clone = [...source];
    const index = Number(head);
    clone[index] = updateValueAtPath(clone[index], rest, nextValue);
    return clone;
  }

  return {
    ...(source || {}),
    [head]: updateValueAtPath(source?.[head], rest, nextValue),
  };
};

export const renameObjectKeyAtPath = (
  source: any,
  objectPath: Array<string | number>,
  oldKey: string,
  nextKey: string
): any => {
  if (!nextKey || nextKey === oldKey) {
    return source;
  }

  if (objectPath.length === 0) {
    if (!source || typeof source !== "object" || Array.isArray(source)) {
      return source;
    }
    const { [oldKey]: oldValue, ...rest } = source;
    if (typeof oldValue === "undefined") {
      return source;
    }
    return {
      ...rest,
      [nextKey]: oldValue,
    };
  }

  const [head, ...restPath] = objectPath;

  if (Array.isArray(source)) {
    const clone = [...source];
    const index = Number(head);
    clone[index] = renameObjectKeyAtPath(clone[index], restPath, oldKey, nextKey);
    return clone;
  }

  return {
    ...(source || {}),
    [head]: renameObjectKeyAtPath(source?.[head], restPath, oldKey, nextKey),
  };
};

export const removeEntryAtPath = (
  source: any,
  containerPath: Array<string | number>,
  entryKey: string | number
): any => {
  if (containerPath.length === 0) {
    if (Array.isArray(source)) {
      const clone = [...source];
      clone.splice(Number(entryKey), 1);
      return clone;
    }

    if (!source || typeof source !== "object") {
      return source;
    }

    const { [entryKey]: removed, ...rest } = source;
    return rest;
  }

  const [head, ...restPath] = containerPath;

  if (Array.isArray(source)) {
    const clone = [...source];
    const index = Number(head);
    clone[index] = removeEntryAtPath(clone[index], restPath, entryKey);
    return clone;
  }

  return {
    ...(source || {}),
    [head]: removeEntryAtPath(source?.[head], restPath, entryKey),
  };
};

export type JsonEntryRowsProps = {
  value: any;
  path?: Array<string | number>;
  depth?: number;
  readOnly?: boolean;
  // When false, entry keys render as plain text even in edit mode (e.g. array-like index keys).
  keyEditable?: boolean;
  lockedKeys?: Set<string>;
  // Display-only transform for non-editable keys (e.g. render 0-based index as 1-based).
  formatKeyLabel?: (key: string) => string;
  onPrimitiveChange?: (path: Array<string | number>, nextValue: string) => void;
  onKeyRename?: (objectPath: Array<string | number>, oldKey: string, nextKey: string) => void;
  onDeleteRequest?: (containerPath: Array<string | number>, entryKey: string | number, label: string) => void;
};

// Shared recursive renderer so Show (read-only) and Edit views render JSON payloads identically.
export const JsonEntryRows: React.FC<JsonEntryRowsProps> = ({
  value,
  path = [],
  depth = 0,
  readOnly = false,
  keyEditable = true,
  lockedKeys = EMPTY_LOCKED_KEYS,
  formatKeyLabel,
  onPrimitiveChange,
  onKeyRename,
  onDeleteRequest,
}) => {
  if (value === null || typeof value !== "object") {
    if (readOnly) {
      return <Typography sx={{ fontSize: 13, lineHeight: 1.4, overflowWrap: "anywhere" }}>{value ?? ""}</Typography>;
    }

    return (
      <MuiTextField
        size="small"
        fullWidth
        multiline
        minRows={1}
        maxRows={6}
        value={value ?? ""}
        onChange={(event) => onPrimitiveChange?.(path, event.target.value)}
        sx={{
          "& .MuiInputBase-input": {
            fontSize: 13,
            lineHeight: 1.4,
            overflowWrap: "anywhere",
          },
        }}
      />
    );
  }

  const entries = Array.isArray(value)
    ? value.map((item, index) => [String(index), item] as const)
    : Object.entries(value);

  const canEditKey = keyEditable && !readOnly && !Array.isArray(value);

  return (
    <Stack spacing={1.25}>
      {entries.map(([key, child]) => (
        <Paper
          key={`${path.join(".")}.${key}`}
          variant="outlined"
          sx={{ p: 1.5, ml: depth > 0 ? 1.5 : 0, backgroundColor: depth % 2 === 0 ? "#fff" : "#fafafa" }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
            <Typography sx={{ color: "text.secondary", fontFamily: "monospace", fontSize: 13, mb: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ flex: 1 }}>
                  {canEditKey ? (
                    <MuiTextField
                      size="small"
                      fullWidth
                      defaultValue={key}
                      disabled={lockedKeys.has(key.toUpperCase())}
                      onBlur={(event) => onKeyRename?.(path, key, event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          onKeyRename?.(path, key, (event.target as HTMLInputElement).value);
                          (event.target as HTMLInputElement).blur();
                        }
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontFamily: "monospace",
                          fontSize: 13,
                        },
                      }}
                    />
                  ) : formatKeyLabel ? (
                    formatKeyLabel(key)
                  ) : (
                    key
                  )}
                </Box>
                {!readOnly && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDeleteRequest?.(path, Array.isArray(value) ? Number(key) : key, String(key))}
                    aria-label={`Delete ${key}`}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Typography>
            <JsonEntryRows
              value={child}
              path={[...path, Array.isArray(value) ? Number(key) : key]}
              depth={depth + 1}
              readOnly={readOnly}
              keyEditable={keyEditable}
              lockedKeys={lockedKeys}
              formatKeyLabel={formatKeyLabel}
              onPrimitiveChange={onPrimitiveChange}
              onKeyRename={onKeyRename}
              onDeleteRequest={onDeleteRequest}
            />
          </Box>
        </Paper>
      ))}
    </Stack>
  );
};
